import React, { useState, useEffect } from 'react';
import Joyride, { STATUS, EVENTS, ACTIONS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';

const OnboardingTour = () => {
  const { user, updateUser } = useAuth();
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if user exists and hasn't completed the tour (0 or null)
    if (user && (user.onboarding_tour === 0 || user.onboarding_tour === null || user.onboarding_tour === undefined)) {
      setRun(true);
      
      // Load saved step index from local storage to allow resuming
      const savedIndex = localStorage.getItem('joyride_step_index');
      if (savedIndex) {
        setStepIndex(parseInt(savedIndex, 10));
      } else {
        setStepIndex(0);
      }
    } else {
      setRun(false);
    }
  }, [user]);

  const steps = [
    {
      target: 'body',
      content: (
        <div className="text-left">
          <h2 className="text-xl font-bold mb-2 text-brand-primary">Willkommen im Team Collaboration Hub!</h2>
          <p>Wir freuen uns, dich an Bord zu haben. Lass uns dir kurz zeigen, wie du das Beste aus der Anwendung herausholst.</p>
        </div>
      ),
      placement: 'center',
      disableBeacon: true,
      route: '/dashboard'
    },
    {
      target: '[data-tour="nav-dashboard"]',
      content: 'Hier findest du das Dashboard mit den wichtigsten Kennzahlen und dem Status deines Teams auf einen Blick.',
      placement: 'right',
      route: '/dashboard'
    },
    {
      target: '[data-tour="dashboard-radar-chart"]',
      content: 'Das Netzdiagramm visualisiert die aktuellen Bewertungen der verschiedenen Themenbereiche (Cluster). Klicke auf die Punkte oder Buttons, um Details zu sehen.',
      placement: 'right',
      route: '/dashboard'
    },
    {
      target: '[data-tour="dashboard-detail-view"]',
      content: 'Hier siehst du die detaillierten Fragen und Ergebnisse für das im Diagramm ausgewählte Element.',
      placement: 'left',
      route: '/dashboard'
    },
    {
      target: '[data-tour="nav-network"]',
      content: 'Im Netzwerk-Bereich siehst du das Wirkungsdiagramm, das die Zusammenhänge zwischen verschiedenen Faktoren und Zielen visualisiert.',
      placement: 'right',
      route: '/network'
    },
    {
      target: '[data-tour="nav-evaluations"]',
      content: 'Hier kannst du Bewertungen abgeben und Fragen beantworten, um den Status deines Teams aktuell zu halten.',
      placement: 'right',
      route: '/evaluations'
    },
    {
      target: '[data-tour="nav-handbook"]',
      content: 'Das Handbuch bietet dir wertvolle Verbesserungsvorschläge und Handlungsempfehlungen basierend auf den Ergebnissen.',
      placement: 'right',
      route: '/handbook'
    },
    {
      target: '[data-tour="rec-impact-score"]',
      content: 'Der Impact Score zeigt dir auf einen Blick, wie viel positiven Einfluss eine Maßnahme auf dein Team hat. Je höher der Wert, desto wirkungsvoller die Maßnahme.',
      placement: 'bottom',
      route: '/handbook'
    },
    ...(user?.teamId ? [
      {
        target: '[data-tour="nav-team-feedback"]',
        content: 'Tausche dich hier mit deinem Team aus, gib Feedback und fördere eine offene Kommunikationskultur.',
        placement: 'right',
        route: '/team-feedback'
      },
      {
        target: '[data-tour="nav-team-management"]',
        content: 'Verwalte hier deine Team-Einstellungen, Mitglieder und spezifische Elemente.',
        placement: 'right',
        route: '/team-management'
      }
    ] : []),
    {
      target: '[data-tour="header-profile"]',
      content: 'Hier oben kannst du dein Profil bearbeiten, Feedback zur App geben oder dich abmelden.',
      placement: 'bottom',
      route: '/profile'
    },
    {
      target: 'body',
      content: (
        <div className="text-left">
          <h2 className="text-xl font-bold mb-2 text-brand-primary">Viel Spaß!</h2>
          <p>Du bist nun bereit, loszulegen. Wir wünschen dir viel Erfolg bei der Zusammenarbeit!</p>
        </div>
      ),
      placement: 'center',
      route: '/dashboard'
    }
  ];

  const handleJoyrideCallback = async (data) => {
    const { status, type, action, index, step } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    // Wenn der Tooltip/Beacon aktiviert wird (z.B. durch Klick auf den blauen Kreis),
    // prüfen wir, ob wir auf der richtigen Seite sind, und navigieren ggf. dorthin.
    if (type === EVENTS.TOOLTIP) {
      if (step && step.route && location.pathname !== step.route) {
        navigate(step.route);
      }
    }

    if (finishedStatuses.includes(status)) {
      setRun(false);
      setStepIndex(0);
      
      // Clear storage when finished
      localStorage.removeItem('joyride_step_index');
      
      // Explizit den Body-Overflow zurücksetzen, um Scroll-Probleme zu beheben
      document.body.style.overflow = 'auto';
      
      try {
        await apiService.updateUser(user.id, {
          onboarding_tour: 1
        });
        
        updateUser({
          ...user,
          onboarding_tour: 1
        });
        
        navigate('/dashboard');
      } catch (error) {
        console.error("Failed to update onboarding status", error);
      }
    } else if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      // Calculate next index based on action
      // Note: If TARGET_NOT_FOUND, we generally want to move forward (skip broken step)
      // unless user clicked PREV.
      const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      
      if (action === ACTIONS.NEXT || action === ACTIONS.PREV || type === EVENTS.TARGET_NOT_FOUND) {
        setStepIndex(nextIndex);
        localStorage.setItem('joyride_step_index', nextIndex);
        
        // Handle navigation for the new step
        if (steps[nextIndex]?.route) {
          navigate(steps[nextIndex].route);
        }
      }
    }
  };

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex} // Controlled mode
      continuous={true}
      showSkipButton={true}
      showProgress={true}
      callback={handleJoyrideCallback}
      disableScrollParentFix={true}
      disableOverlayClose={true} // Prevent closing when clicking overlay
      styles={{
        options: {
          primaryColor: '#0056b3', // brand-primary
          zIndex: 10000,
        },
        buttonNext: {
          backgroundColor: '#0056b3',
          color: '#ffffff',
          borderRadius: '0.375rem',
          padding: '0.5rem 1rem',
          fontWeight: '500',
        },
        buttonBack: {
          color: '#6b7280',
          marginRight: '10px',
        },
        buttonSkip: {
          color: '#6b7280',
        },
        tooltip: {
          borderRadius: '0.5rem',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipTitle: {
          fontWeight: 'bold',
          color: '#111827',
        },
      }}
      locale={{
        back: 'Zurück',
        close: 'Schließen',
        last: 'Fertig',
        next: 'Weiter',
        skip: 'Überspringen',
      }}
    />
  );
};

export default OnboardingTour;